import { useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { GetStartedTemplates } from './components/get-started-templates';
import { ManageUsersCard } from './components/manage-users-card';
import { MatterIssues } from './components/matter-issues';
import { MatterSummary } from './components/matter-summary';
import { ProceduralTimelinePreview } from './components/procedural-timeline-preview';
import { ProcessingPipeline } from './components/processing-pipeline';
import { RecentActivity } from './components/recent-activity';
import { RecentKimThreads } from './components/recent-kim-threads';
import { RecentRunStatus, StatusCard } from './components/recent-run-status';
import {
  NewKimLargeInput,
  type NewKimLargeInputHandle,
} from './subsets/:subsetId/new-kim/components/new-kim-large-input';
import { WEXLER_PROMPT_TEMPLATES } from './subsets/:subsetId/new-kim/templates/wexler-prompt-templates';
import { Permission } from '../../../../../../backend/src/auth/permissions/permission';
import { OrgPermission } from '../../../../../../backend/src/organisation/organisation-permission';
import { MatterType, RunStatus } from '../../../../../../backend/src/types/types';
import { PageName, TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent, trackPageView } from '@/analytics/Mixpanel';
import { useImportSampleMatter } from '@/api/mutations/useImportSampleMatter';
import { useGetMatterOverview } from '@/api/queries/useGetMatterOverview';
import { useGetMatterV2 } from '@/api/queries/useGetMatterV2';
import { useGetProceduralTimelineEvents } from '@/api/queries/useGetProceduralTimelineEvents';
import { useGetRecentActivity } from '@/api/queries/useGetRecentActivity';
import { useGetSubset } from '@/api/queries/useGetSubset';
import { useGetSubsetOverview } from '@/api/queries/useGetSubsetOverview';
import { useGetSubsets, hasRecentProcessingSubset } from '@/api/queries/useGetSubsets';
import { useGetSummary } from '@/api/queries/useGetSummary';
import { useUserContext } from '@/Contexts/User';
import { formatDuration } from '@/helpers/formatDate';
import { hasAnyOngoingRunOnAllDocsCase } from '@/helpers/runs';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { PermissionGate } from '@/hooks/useHasPermission';
import { Breadcrumb } from '@/new/components/breadcrumb';
import { useMatterBreadcrumbHead } from '@/new/components/breadcrumb-dropdown';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { TextWithReferences } from '@/new/components/text-references';
import { useSubsetBreadcrumbs } from '@/new/hooks/use-subset-breadcrumbs';
import { PipelineProcessingStages, type ProcessingStatus } from '@/new/lib/pipeline-status';
import { cn } from '@/new/lib/utils';
import { useMatterProcessingStore } from '@/new/stores/matter-processing-store';
import { Summary as IssueSummary } from '@/types';

export function MatterOverview() {
  const { matterId, subsetId } = useParams();
  if (!matterId) return null;

  const navigate = useNavigate();
  const { i18n } = useTranslation();

  const kimInputRef = useRef<NewKimLargeInputHandle>(null);

  // Prefill the Kim input on the right from the "Get started" cards. A card can either reference a
  // Wexler template by id, or pass a ready-to-send custom Kim question directly.
  const prefillKimTemplate = (templateId: string, customPrompt?: string) => {
    if (customPrompt) {
      trackCustomEvent(TrackingEventsV2.KimUseTemplate, { matterId, templateSource: 'custom', template: templateId });
      kimInputRef.current?.prefill(customPrompt);
      return;
    }
    const template = WEXLER_PROMPT_TEMPLATES.find((t) => t.id === templateId);
    if (!template) return;
    trackCustomEvent(TrackingEventsV2.KimUseTemplate, { matterId, templateSource: 'wexler', template: templateId });
    kimInputRef.current?.prefill(template.template);
  };

  const { isEnabled } = useOrgPermissions();
  const { user } = useUserContext();
  const { data: matter, isLoading: isLoadingMatter } = useGetMatterV2(matterId);
  const { data: subset, isLoading: isLoadingSubset } = useGetSubset(matterId, subsetId);

  // A `subsetId` route shows the subset view, except "All Documents" which is the matter itself.
  const isSubsetRoute = !!subsetId;
  const isSubsetMode = isSubsetRoute && subset?.isAllDocuments !== true;

  useEffect(() => {
    trackPageView(isSubsetMode ? PageName.SubsetSummary : PageName.MatterSummary);
  }, [isSubsetMode]);

  // The subset this page is scoped to: the route subset, or the all-documents subset in matter mode.
  const effectiveSubsetId = subsetId ?? matter?.allDocumentsCaseId ?? undefined;

  const { mutateAsync: importSampleMatter, isLoading: isImportingSampleMatter } = useImportSampleMatter();

  const { data: subsets, refetch: refetchSubsets } = useGetSubsets(matterId);
  const hasProcessingSubset = hasRecentProcessingSubset(subsets);

  const isCreator = subset?.createdById === user?.id;

  // Processing state, computed in the breadcrumb.
  const processingState = useMatterProcessingStore((state) => state.getProcessingState(matterId));

  // Is a run going on the subset this page is scoped to?
  const areRunsProcessing = hasAnyOngoingRunOnAllDocsCase(processingState?.allProcessingRuns, effectiveSubsetId ?? '');
  const subsetRuns = processingState?.allProcessingRuns?.filter((run) => run.caseId === effectiveSubsetId) ?? [];

  const isSubsetProcessing = subset?.status === 'processing';
  const isSubsetGenerating = isSubsetProcessing || areRunsProcessing;

  const {
    data: matterOverview,
    isLoading: isLoadingMatterOverview,
    refetch: refetchMatterOverview,
  } = useGetMatterOverview(matterId);
  const {
    data: subsetOverview,
    isLoading: isLoadingSubsetOverview,
    refetch: refetchSubsetOverview,
  } = useGetSubsetOverview(matterId, subsetId, { isProcessing: isSubsetGenerating });

  const overview = isSubsetMode ? subsetOverview : matterOverview;
  const isLoadingOverview = isSubsetMode ? isLoadingSubsetOverview : isLoadingMatterOverview;
  const refetchOverview = isSubsetMode ? refetchSubsetOverview : refetchMatterOverview;

  const {
    data: summaries,
    isLoading: summaryIsLoading,
    refetch: refetchSummaries,
  } = useGetSummary({
    matterId,
    subsetId: effectiveSubsetId,
  });

  const hasProceduralTimelines = isEnabled(OrgPermission.ProceduralTimelines);
  const { isLoading: isLoadingTimelineQuery } = useGetProceduralTimelineEvents(matterId, {
    enabled: hasProceduralTimelines,
  });
  const isLoadingTimeline = hasProceduralTimelines && isLoadingTimelineQuery;

  const { data: recentActivity, isLoading: isLoadingRecentActivity } = useGetRecentActivity(
    matterId,
    isSubsetMode ? subsetId : undefined,
  );

  // Matter may still be loading; the head shows a single skeleton until the name resolves.
  const matterBreadcrumbItems = useMatterBreadcrumbHead(matterId, matter?.name, true);

  const subsetBreadcrumbItems = useSubsetBreadcrumbs({
    matterId,
    subsetId: subsetId ?? '',
    matter,
    subset,
    currentPage: null, // This is the subset overview page
  });

  // Skeleton the whole left panel until all its content is ready.
  const isSummaryPanelLoading =
    isLoadingMatter ||
    (isSubsetMode && isLoadingSubset) ||
    isLoadingOverview ||
    summaryIsLoading ||
    isLoadingRecentActivity ||
    (!isSubsetMode && isLoadingTimeline);

  const [selectedIssue, setSelectedIssue] = useState<IssueSummary | null>(null);
  const issueContentRef = useRef<HTMLDivElement>(null);
  const [isIssueScrolledToBottom, setIsIssueScrolledToBottom] = useState(true);

  // Fade out the bottom of the issue content while it's overflowing and not scrolled to the end.
  useEffect(() => {
    const el = issueContentRef.current;
    if (!el) return;

    const checkScroll = () => {
      setIsIssueScrolledToBottom(el.scrollHeight - el.scrollTop <= el.clientHeight + 1);
    };

    checkScroll();
    const observer = new ResizeObserver(checkScroll);
    observer.observe(el);
    return () => observer.disconnect();
  }, [selectedIssue]);

  const handleCreateSampleMatter = async () => {
    trackCustomEvent(TrackingEventsV2.MatterCreateSample, { source: 'matter_homepage_processing' });
    await importSampleMatter(undefined, {
      onSuccess: (data) => {
        navigate(`/matters/${data.matterId}`);
      },
    });
  };

  // `undefined` until the breadcrumb derives it from the subsets query. Render nothing until then.
  const isProcessingStateKnown = processingState != null;

  // In subset mode the right panel needs the subset loaded too, or the pipeline flashes before Kim.
  const isRightPanelReady = isProcessingStateKnown && (!isSubsetMode || (!isLoadingSubset && subset != null));

  // "Completed" = subset finished its initial generation (a recontextualising run on a `ready`
  // subset doesn't block Kim), or in matter mode any run across the matter has completed.
  const hasCompletedRuns = isSubsetMode ? subset?.status === 'ready' : (processingState?.hasCompletedRuns ?? false);

  // First processing run (sorted with awaiting-approval first), subset-scoped in subset mode.
  const mostRecentProcessingRun = (isSubsetMode ? subsetRuns : processingState?.allProcessingRuns)?.[0] ?? null;

  // Most recent run for the status card (matter-wide vs subset-scoped).
  const mostRecentRun = isSubsetMode ? (subsetRuns[0] ?? null) : (processingState?.mostRecentRun ?? null);

  // "X - Y" time remaining estimate. The lower bound is optional, so we may show only an upper bound.
  const estimateRun = mostRecentProcessingRun ?? mostRecentRun;
  const estimateUpper = estimateRun?.completedAtEstimate ?? null;
  const estimateLower = estimateRun?.completedAtEstimateLower ?? null;
  const timeEstimateText = estimateUpper
    ? (() => {
        const now = Date.now();
        const upperText = formatDuration(new Date(estimateUpper).getTime() - now);
        const lowerMs = estimateLower ? new Date(estimateLower).getTime() - now : null;
        const lowerText = lowerMs != null && lowerMs >= 60_000 ? formatDuration(lowerMs) : null;
        return lowerText ? `${lowerText} - ${upperText}` : upperText;
      })()
    : null;

  // Only show the estimate from Extracting onwards — earlier stages already surface a duration.
  const estimateRunStatus = estimateRun?.status ?? null;
  const isExtractingOrLater =
    estimateRunStatus != null &&
    PipelineProcessingStages.indexOf(estimateRunStatus as ProcessingStatus) >=
      PipelineProcessingStages.indexOf(RunStatus.Extracting);

  useEffect(() => {
    if (!areRunsProcessing && matterId && effectiveSubsetId) {
      refetchOverview();
      refetchSummaries();
      refetchSubsets();
    }
  }, [areRunsProcessing, refetchOverview, refetchSummaries, refetchSubsets, matterId, effectiveSubsetId]);

  // When a subset transitions from processing -> ready, refetch its overview and summary.
  const prevSubsetStatus = useRef(subset?.status);
  useEffect(() => {
    if (prevSubsetStatus.current === 'processing' && subset?.status === 'ready') {
      refetchOverview();
      refetchSummaries();
    }
    prevSubsetStatus.current = subset?.status;
  }, [subset?.status, refetchOverview, refetchSummaries]);

  const issues = summaries?.content?.slice(1);

  const displayName = isSubsetMode ? subset?.name : matter?.name;
  const displayCreatedDate = isSubsetMode ? subset?.createdDate : matter?.createdDate;

  return (
    <>
      <Breadcrumb items={isSubsetRoute ? subsetBreadcrumbItems : matterBreadcrumbItems} />

      <div className="flex h-full w-full overflow-hidden bg-gray-50">
        <div className="relative m-2 w-[40rem] min-w-[20rem] shrink-1">
          <div className="h-full overflow-y-auto rounded-lg border bg-white pb-1">
            <AnimatePresence mode="wait">
              {isSummaryPanelLoading ? (
                <motion.div
                  key="summary-panel-skeleton"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <SummaryPanelSkeleton />
                </motion.div>
              ) : (
                <motion.div
                  key="summary-panel-content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <div className="sticky top-0 z-10 border-b bg-white px-6.5 py-5.5">
                    <div className="mx-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-3 gap-y-1">
                        <div className="flex items-center gap-2.5">
                          {isSubsetMode && <Icon className="lucide--group -mb-0.5 text-lg text-gray-400" />}
                          <h1 className="font-serif text-2xl font-medium">{displayName}</h1>
                        </div>

                        {!isSubsetMode && matter?.code && (
                          <div className="-mb-0.5 w-fit rounded-md border bg-gray-50 p-1.5 py-0.25 font-mono text-[13px] text-gray-600">
                            {matter.code}
                          </div>
                        )}

                        {!isSubsetMode && (
                          <PermissionGate
                            uiMode="disabled"
                            permission={[
                              Permission.MatterEdit,
                              Permission.DocumentDelete,
                              Permission.DocumentDownload,
                              Permission.RelativityImport,
                              Permission.MatterViewUsers,
                              Permission.MatterCreate,
                              Permission.AuditView,
                            ]}
                            disabledMessage="You do not have permission to access Matter settings"
                          >
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button variant="ghost" className="-mb-0.5 px-0 text-base text-gray-400" asChild>
                                  <Link
                                    to={`/matters/${matterId}/settings`}
                                    onClick={() =>
                                      trackCustomEvent(TrackingEventsV2.MatterViewSettingsFromOverview, { matterId })
                                    }
                                  >
                                    <Icon className="lucide--settings" />
                                  </Link>
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent side="top" sideOffset={6}>
                                Matter settings
                              </TooltipContent>
                            </Tooltip>
                          </PermissionGate>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-[13px] text-gray-500">
                        <p>
                          {displayCreatedDate &&
                            `Created ${new Date(displayCreatedDate).toLocaleDateString(i18n.language, {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                            })}`}
                        </p>

                        {!isSubsetMode && (
                          <>
                            <span aria-hidden className="text-xs text-gray-300">
                              &bull;
                            </span>

                            <Link
                              to={`/matters/${matterId}/settings/users`}
                              onClick={() =>
                                trackCustomEvent(TrackingEventsV2.MatterViewUsersFromOverview, { matterId })
                              }
                              className="group flex items-center gap-1 transition-colors hover:text-blue-500"
                            >
                              <span>
                                <span className="font-medium text-gray-700 duration-150 group-hover:text-blue-500">
                                  {matter?.users?.length ?? 0}
                                </span>{' '}
                                {matter?.users?.length === 1 ? 'User' : 'Users'}
                              </span>
                              <Icon className="lucide--arrow-up-right" />
                            </Link>
                          </>
                        )}

                        {hasCompletedRuns && (
                          <>
                            <span aria-hidden className="text-xs text-gray-300">
                              &bull;
                            </span>

                            <Link
                              to={`/matters/${matterId}/subsets/${effectiveSubsetId}/docs`}
                              onClick={() =>
                                trackCustomEvent(TrackingEventsV2.SubsetViewDocumentsFromSummary, {
                                  matterId,
                                  subsetId: effectiveSubsetId,
                                  isAllDocuments: !isSubsetMode,
                                })
                              }
                              className="group flex items-center gap-1 transition-colors hover:text-blue-500"
                            >
                              <span className="font-medium text-gray-700 duration-150 group-hover:text-blue-500">
                                {overview?.documents?.toLocaleString() ?? 0}
                              </span>{' '}
                              {overview?.documents === 1 ? 'Document' : 'Documents'}
                              <Icon className="lucide--arrow-up-right" />
                            </Link>

                            <span aria-hidden className="text-xs text-gray-300">
                              &bull;
                            </span>

                            <Link
                              to={`/matters/${matterId}/subsets/${effectiveSubsetId}/facts`}
                              onClick={() =>
                                trackCustomEvent(TrackingEventsV2.SubsetViewFactsFromSummary, {
                                  matterId,
                                  subsetId: effectiveSubsetId,
                                  isAllDocuments: !isSubsetMode,
                                })
                              }
                              className="group flex items-center gap-1 transition-colors hover:text-blue-500"
                            >
                              <span className="font-medium text-gray-700 duration-150 group-hover:text-blue-500">
                                {overview?.totalFacts?.toLocaleString() ?? 0}
                              </span>{' '}
                              {overview?.totalFacts === 1 ? 'Fact' : 'Facts'}
                              <Icon className="lucide--arrow-up-right" />
                            </Link>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Show overview or add users prompt depending on if there are completed runs */}
                  {hasCompletedRuns ? (
                    <>
                      <AnimatePresence>
                        {selectedIssue && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.1 }}
                            style={{ transformOrigin: 'left' }}
                            className="absolute top-0 bottom-0 left-full z-20 mx-2 flex w-[28rem] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border bg-white"
                          >
                            <Button
                              variant="ghost"
                              size="icon"
                              className="absolute top-1 right-1 text-gray-400 hover:text-gray-700"
                              onClick={() => setSelectedIssue(null)}
                            >
                              <Icon className="lucide--x" />
                            </Button>

                            <div className="mx-6 mt-5">
                              <p className="flex items-center gap-1 text-[13px] text-gray-500">
                                <Icon className="lucide--library-big mb-px text-gray-400" /> Issue
                              </p>
                              <h2 className="mt-0.75 flex items-center gap-2 font-serif text-2xl font-medium text-gray-900">
                                {selectedIssue.name}
                              </h2>
                            </div>

                            <div
                              ref={issueContentRef}
                              onScroll={(e) => {
                                const target = e.currentTarget;
                                setIsIssueScrolledToBottom(
                                  target.scrollHeight - target.scrollTop <= target.clientHeight + 1,
                                );
                              }}
                              className="my-4 mb-5 flex flex-col gap-3 overflow-y-auto mask-b-from-80% mask-b-to-99% px-6 text-[13px] leading-relaxed text-gray-700 transition-[mask-size]"
                              style={{ maskSize: isIssueScrolledToBottom ? '100% 130%' : '100% 100%' }}
                            >
                              <TextWithReferences content={selectedIssue.content} />
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {mostRecentRun && (
                        <div className="mx-5 my-4.5">
                          <RecentRunStatus mostRecentRun={mostRecentRun} allDocumentsCaseId={effectiveSubsetId} />
                        </div>
                      )}

                      {recentActivity && recentActivity.length > 0 ? (
                        <div className="mx-7.5 my-4.5">
                          <RecentActivity
                            matterId={matterId}
                            subsetId={isSubsetMode ? subsetId : undefined}
                            activity={recentActivity ?? []}
                          />
                        </div>
                      ) : (
                        // Get-started cards are matter-only; subsets just show recent activity.
                        !isSubsetMode && (
                          <div className="mx-7.5 my-4.5">
                            <GetStartedTemplates onSelectTemplate={prefillKimTemplate} />
                          </div>
                        )
                      )}

                      <div className="mx-7.5">
                        <MatterSummary
                          content={summaries?.content?.[0]?.content ?? ''}
                          context={isSubsetMode ? 'Subset' : 'Matter'}
                        />

                        {issues && issues.length > 0 && (
                          <MatterIssues
                            issues={issues}
                            onSelectIssue={(issue) => {
                              trackCustomEvent(TrackingEventsV2.MatterViewIssueFromOverview, {
                                matterId,
                                subsetId: isSubsetMode ? subsetId : undefined,
                                issueName: issue.name,
                              });
                              setSelectedIssue(issue);
                            }}
                            context={isSubsetMode ? 'Subset' : 'Matter'}
                          />
                        )}

                        {!isSubsetMode && hasProceduralTimelines && <ProceduralTimelinePreview matterId={matterId} />}
                      </div>
                    </>
                  ) : (
                    <div className="my-4.5">
                      {isSubsetMode && (
                        <div className="mx-5 my-4.5">
                          <StatusCard
                            variant="sky"
                            iconClassName="lucide--loader animate-spin text-sky-500 ease-in-out"
                            title="Creating Subset"
                            subText="We're generating this Subset from the documents and issues you selected. You can leave this page — we'll keep working in the background."
                          />
                        </div>
                      )}

                      {mostRecentRun && (
                        <div className="mx-5 my-4.5">
                          <RecentRunStatus
                            mostRecentRun={mostRecentRun}
                            allDocumentsCaseId={effectiveSubsetId}
                            approvalOnly
                          />
                        </div>
                      )}

                      {timeEstimateText && isExtractingOrLater && (
                        <div className="mx-7.5 my-4.5">
                          <h2 className="mb-1 font-medium">Estimated time remaining</h2>

                          <p className="animate-pulse text-gray-800">{timeEstimateText}</p>
                        </div>
                      )}

                      {!isSubsetMode && (
                        <>
                          {/* Option to create sample matter */}
                          <div className="mx-7.5 my-4.5 mt-6">
                            <button
                              type="button"
                              onClick={handleCreateSampleMatter}
                              disabled={isImportingSampleMatter}
                              className={cn(
                                'group flex w-full cursor-pointer items-center gap-4 text-left',
                                isImportingSampleMatter && 'animate-pulse opacity-50',
                              )}
                            >
                              <div className="relative h-14 w-31 shrink-0 overflow-hidden rounded-lg border border-gray-300/80 bg-gray-50 duration-150 group-hover:border-gray-400/60">
                                {/* Mini app-shell preview: sidebar on the gradient + inset content panel */}
                                <div className="absolute top-1/2 left-1/2 mt-3.5 flex h-14 w-23 -translate-x-1/2 -translate-y-1/2 gap-1.25 overflow-hidden rounded-lg border border-gray-300/80 p-1 shadow-lg/4 duration-150 group-hover:-translate-y-[calc(50%+2px)] group-hover:border-gray-300 group-hover:shadow-lg/5">
                                  {/* Sidebar — sits directly on the gradient, like the real app */}
                                  <div className="w-4.5 shrink-0 space-y-1 pt-0.5">
                                    <div className="h-1.25 w-full rounded-sm bg-gray-300/80"></div>
                                    <div className="h-1.25 w-4/5 rounded-sm bg-gray-300/80"></div>
                                    <div className="h-1.25 w-full rounded-sm bg-gray-300/80"></div>
                                  </div>
                                  {/* Inset white content panel */}
                                  <div className="flex-1 space-y-1 rounded-sm border border-black/10 bg-white p-1.5">
                                    <div className="mb-1.5 h-1.5 w-8 rounded-xs bg-gray-300"></div>
                                    <div className="h-1.25 w-full rounded-sm bg-gray-100"></div>
                                    <div className="h-1.25 w-3/4 rounded-sm bg-gray-100"></div>
                                  </div>
                                </div>
                              </div>
                              <div className="space-y-0.5">
                                <h2 className="flex items-center gap-0.5 font-medium">
                                  Browse a sample Matter while you wait{' '}
                                  <Icon className="lucide--chevron-right ml-1 text-gray-500 duration-150 group-hover:ml-1.5 group-hover:text-teal-500" />
                                </h2>
                                <p className="text-[13px] text-gray-500">
                                  Get familiar with Wexler and Kim by exploring a pre-processed Matter.
                                </p>
                              </div>
                            </button>
                          </div>

                          <div className="mx-7.5 my-4.5">
                            <h2 className="mb-1 font-medium">Manage users</h2>
                            <p className="mb-4 text-[13px] text-gray-500">
                              Add other users to this Matter while you wait for processing to complete.
                            </p>

                            <ManageUsersCard matterId={matterId} />
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Kim if there are completed runs, else the pipeline. Render nothing until we know which.
            A subset mid-creation shows nothing here — the left panel carries that message instead. */}
        <AnimatePresence mode="wait">
          {!isRightPanelReady || (isSubsetMode && !hasCompletedRuns) ? null : hasCompletedRuns ? (
            <motion.div
              key="kim"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="relative flex grow flex-col items-center justify-center overflow-y-auto py-8"
            >
              <AnimatePresence>
                {!isSummaryPanelLoading && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="absolute top-5 right-5 z-10 flex items-center gap-2"
                  >
                    {isSubsetMode ? (
                      <PermissionGate permission={Permission.SubsetEdit} orAllowed={isCreator} uiMode="disabled">
                        <Button variant="primary" className="rounded-lg" asChild disabled={isSubsetGenerating}>
                          <Link
                            to={`/matters/${matterId}/subsets/${subsetId}/edit`}
                            onClick={() => trackCustomEvent(TrackingEventsV2.SubsetEditFromSummary, { subsetId })}
                            className={cn(isSubsetGenerating && 'pointer-events-none opacity-50')}
                          >
                            <Icon className="lucide--group" />
                            Edit Subset Issues or Documents
                          </Link>
                        </Button>
                      </PermissionGate>
                    ) : (
                      <>
                        {hasCompletedRuns && (
                          <PermissionGate permission={Permission.SubsetCreate} uiMode="disabled">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span tabIndex={hasProcessingSubset ? 0 : undefined}>
                                  <Button
                                    variant="white"
                                    className="rounded-lg"
                                    asChild
                                    data-tour="create-subset-btn"
                                    disabled={hasProcessingSubset}
                                  >
                                    <Link
                                      to={`/matters/${matterId}/subsets/new`}
                                      onClick={() =>
                                        trackCustomEvent(TrackingEventsV2.SubsetOpenCreateNew, { matterId })
                                      }
                                      className={cn(hasProcessingSubset && 'pointer-events-none opacity-50')}
                                    >
                                      <Icon className="lucide--group" />
                                      <span>Create a Subset</span>
                                    </Link>
                                  </Button>
                                </span>
                              </TooltipTrigger>
                              {hasProcessingSubset && (
                                <TooltipContent>
                                  Please wait for existing Subsets to finish being created before creating a new Subset.
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </PermissionGate>
                        )}

                        {!matter?.isDraft && (
                          <PermissionGate permission={Permission.DocumentUpload} uiMode="disabled">
                            {matter?.type === MatterType.Sample ? (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span>
                                    <Button variant="primary" className="rounded-lg" disabled>
                                      <Icon className="lucide--file-up" />
                                      Upload Documents
                                    </Button>
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent>You can't upload documents to a sample Matter.</TooltipContent>
                              </Tooltip>
                            ) : matter?.issues ? (
                              <Button variant="primary" className="rounded-lg" asChild>
                                <Link to={`/matters/${matterId}/docs/upload`}>
                                  <Icon className="lucide--file-plus-2" />
                                  Upload Documents
                                </Link>
                              </Button>
                            ) : (
                              <div className="flex items-center gap-2 px-2 text-xs text-gray-600">
                                <Icon className="lucide--info" />
                                <p>
                                  <Link className="text-blue-500" to={'settings/context'}>
                                    Add Matter issues
                                  </Link>
                                  <span> to upload more documents</span>
                                </p>
                              </div>
                            )}
                          </PermissionGate>
                        )}
                      </>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="flex w-full items-center justify-center px-4">
                <NewKimLargeInput
                  ref={kimInputRef}
                  matterId={matterId}
                  subsetId={effectiveSubsetId}
                  headingText="Welcome back"
                  placeholder={isSubsetMode ? 'What documents are in this Subset?' : undefined}
                  kimInputClassName="min-h-20 max-h-32"
                  threadSource={isSubsetMode ? 'subset_overview' : 'matter_overview'}
                  onFocus={() => setSelectedIssue(null)}
                />
              </div>

              <RecentKimThreads matterId={matterId} subsetId={effectiveSubsetId} isSubsetMode={isSubsetMode} />
            </motion.div>
          ) : (
            <motion.div
              key="pipeline"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="grow overflow-y-auto py-28"
            >
              <ProcessingPipeline
                casePipelineStatus={mostRecentProcessingRun?.status}
                requiresAdminApproval={mostRecentProcessingRun?.requiresAdminApproval}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
}

function SummaryPanelSkeleton() {
  return (
    <>
      {/* Matter name + stats */}
      <div className="border-b px-6.5 py-5.5">
        <div className="mx-1 space-y-2.5">
          <Skeleton className="h-7 w-2/3" />
          <div className="flex items-center gap-2">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
          </div>
        </div>
      </div>

      <div className="mx-7.5">
        {/* Summary */}
        <div className="my-4.5">
          <Skeleton className="my-4.5 h-5 w-40" />
          <div className="space-y-2">
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-11/12" />
            <Skeleton className="h-3.5 w-full" />
            <Skeleton className="h-3.5 w-4/5" />
          </div>
        </div>

        {/* Issues */}
        <div className="my-5">
          <Skeleton className="mb-4.5 h-5 w-32" />
          <div className="flex flex-wrap gap-1.5">
            <Skeleton className="h-7 w-28 rounded-xl" />
            <Skeleton className="h-7 w-36 rounded-xl" />
            <Skeleton className="h-7 w-32 rounded-xl" />
          </div>
        </div>

        {/* Procedural timeline */}
        <div className="my-4.5 mt-6">
          <Skeleton className="mb-4.5 h-5 w-44" />
        </div>
      </div>
    </>
  );
}
